import React, { useContext, useState } from "react";
import ZipModal from "./hoa-zip-modal";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import DownloadIcon from "@mui/icons-material/GetApp";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { Divider, IconButton, Stack, Tooltip } from "@mui/material";
import { urlCheck } from "../utility/helper";
import config from "../hoa-config";
import { GenerateNewDocumentUrl } from "../utility/generate-new-document-url";
import datastore from "../store/hoa-datastore";
import { GlobalContext } from "../context/hoa-global-context";

const SubTitle: React.FC<{ title: string }> = ({ title }) => (
	<span style={{ minWidth: "300px" }} className="text-787878-16px bold">
		{title}
	</span>
);
const SubValues: React.FC<{ value: string }> = ({ value }) => (
	<span style={{ whiteSpace: 'pre-line' }} className="text-303030-16px normal">{value}</span>
);
const Container: React.FC<{ children: any; sx?: any }> = ({ children, sx }) => (
	<Stack gap={2} sx={sx} alignItems={"center"} flexDirection={"row"}>
		{children}
	</Stack>
);

const blueContainerForArray = (value: any, title: string) => {
	return (
		<Container>
			<SubTitle title={title} />
			{value.map((title: any, index: number) => {
				return (
					<div
						key={index}
						style={{ padding: "8px 8px 8px 12px", marginTop: "5px" }}
						className="communuty-service-single-container"
					>
						<span className="text-5B5B5B-14px">{title}</span>
					</div>
				);
			})}
		</Container>
	);
};

const blueArrayContainer = (value: any, title: string) => {
	return (
		<Container sx={{ flexWrap: "wrap" }}>
			{value.map((title: any, index: number) => {
				return (
					<div
						key={index}
						style={{ padding: "8px 8px 8px 12px", marginTop: "5px" }}
						className="communuty-service-single-container"
					>
						<span className="text-5B5B5B-14px">{title}</span>
					</div>
				);
			})}
		</Container>
	);
};

const regulationDataChange = (title: string) => {
	let tmpTitle = "Regulations";
	if (config.violationRegulations[title] === "typesOfCommercialVehicle") {
		tmpTitle = "Types of Commercial Vehicles";
	} else if (config.violationRegulations[title] === "instructionsOfCelebrationCertainFestivals") {
		tmpTitle = "Instructions";
	}
	return tmpTitle;
};

const PdfContainer: React.FC<{ documents: any }> = ({
	documents,
}) => {
	if (documents?.length === 0) return null;

	const onFileDownload = async (url: string) => {
		const newUrl = await GenerateNewDocumentUrl.getNewUrl(url, "hoa");
		window.open(urlCheck(newUrl), "_blank");
	};

	return (
		<Stack flexDirection={"column"} alignItems={"center"}>
			{documents.map((file: any, fileIndex: any) => {
				return (
					<div
						key={fileIndex}
						className="ov-pdf-container"
						onClick={() => onFileDownload(file.url)}
						style={{ cursor: file?.fileName ? "pointer" : "not-allowed" }}
					>
						<div style={{ display: "flex", alignItems: "center" }}>
							<PictureAsPdfIcon style={{ width: "20px", height: "20px" }} />
							<span style={{ marginLeft: "14px" }} className="text-3E3E3E-14px">
								{file.fileName || "N/A"}
							</span>
						</div>
						<DownloadIcon style={{ width: "20px", height: "20px", color: "#3576E5" }} />
					</div>
				);
			})}
		</Stack>
	);
};

const OpenANewLinkContainer: React.FC<{ title: string; value: string }> = ({ title, value }) => {
	return (
		<Container>
			<SubTitle title={title} />
			<div>
				<span className="text-3576E5-14px">Open Link</span>
				<IconButton
					onClick={() => {
						if (value !== "N/A") {
							window.open(urlCheck(value), "_blank");
						}
					}}
				>
					<OpenInNewIcon
						style={{
							width: "20px",
							height: "20px",
							color: "#3576E5",
						}}
					/>
				</IconButton>
			</div>
		</Container>
	);
};

export const StringComponentForOverView: React.FC<{ title: string; value: string }> = ({
	title,
	value,
}) => {
	if (title === "HOA Portal link" || title === "PMC Portal link") {
		return <OpenANewLinkContainer title={title} value={value} />;
	}

	return (
		<Container>
			<SubTitle title={title} />
			<SubValues value={value} />
		</Container>
	);
};

export const ArrayComponentForOverView: React.FC<{ title: string; value: string[] }> = ({
	title,
	value,
}) => {
	if (title === "Community Services") {
		return blueContainerForArray(value, title);
	}
	if (title === "clientInformation") {
		return (
			<Stack gap={1} flexDirection={"column"}>
				{value.map((client: any) => {
					return (
						<Stack
							p={1}
							gap={1}
							sx={{
								width: "300px",
								background: "#FAFAFA",
								borderRadius: "4px",
							}}
							border={"1px solid #E8E8E8"}
						>
							<span className="text-303030-16px normal">{client.clientName}</span>
							<span className="text-303030-16px normal">{`Vendor ID: ${client.vendorID}`}</span>
						</Stack>
					);
				})}
			</Stack>
		);
	}
	if (title === "Zip") {
		const [zipOpen, setZipOpen] = useState(false);

		function summarizeMunicipalityInfo(value: any) {
			const info = value;
			if (info.length > 5) {
				return `${info.slice(0, 5).join(", ")} +${info.length - 5} More`;
			} else {
				return info.join(", ");
			}
		}

		return (
			<>
				<Container>
					<SubTitle title={title} />
					<span
						onClick={() => {
							setZipOpen(true);
						}}
					>
						{summarizeMunicipalityInfo(value)}
					</span>
				</Container>
				<ZipModal
					open={zipOpen}
					onClose={() => {
						setZipOpen(!zipOpen);
					}}
				/>
			</>
		);
	}
	if (title === "Gate Access Form") {
		return (
			<Container>
				<SubTitle title={title} />
				<PdfContainer documents={value} />
			</Container>
		);
	}

	return (
		<Container>
			<SubTitle title={title} />
			<SubValues value={value.join(", ")} />
		</Container>
	);
};

export const ObjectComponentForOverView: React.FC<{
	processItem: any;
	value: any;
	title: string;
}> = ({ processItem, value, title }) => {
	return processItem(value);
};

export const StringComponentForProcedure: React.FC<{ title: string; value: string }> = ({
	title,
	value,
}) => {
	if (title === "Tenant or Lease Registration/Renewal Form") {
		return <OpenANewLinkContainer title={title} value={value} />;
	}

	return (
		<Container>
			<SubTitle title={title} />
			<SubValues value={value} />
		</Container>
	);
};

export const ArrayComponentForProcedure: React.FC<{ title: string; value: string[] }> = ({
	title,
	value,
}) => {
	if (title === "Categories") {
		const { selectedValues } = useContext<any>(GlobalContext);

		const getTooltipText = (category: string) => {
			if (
				!category ||
				category?.length === 0 ||
				!Array.isArray(selectedValues?.procedures.keywords)
			) {
				return null;
			}

			const selectedKeywords = selectedValues.procedures.keywords.filter((keyword: string) =>
				category.includes(config.leasingRestrictionsAndRegulationsCategory[keyword])
			);

			return (
				<div style={{ display: "flex", flexDirection: "column" }}>
					<div style={{ marginTop: "5px", marginBottom: "5px", fontWeight: "bold" }}>
						Keywords
					</div>
					<Divider style={{ marginBottom: "5px", background: "#FFF" }} />
					{selectedKeywords.map((keyword: string, index: number) => (
						<div key={index} style={{ marginBottom: "5px" }}>
							{keyword}
						</div>
					))}
				</div>
			);
		};

		return (
			<Stack alignItems={"center"} gap={1} flexDirection={"row"}>
				<SubTitle title={title} />
				{value.map((category: string, index: number) => {
					return (
						<Stack
							border={"1px solid #E8E8E8"}
							alignItems={"center"}
							flexDirection={"row"}
							gap={1}
							p={1}
							borderRadius={"4px"}
							key={index}
							sx={{ backgroundColor: "#FAFAFA" }}
						>
							<span className="text-3E3E3E-14px">{category}</span>
							<Tooltip title={getTooltipText(category)}>
								<ErrorOutlineIcon style={{ fontSize: "20px" }} />
							</Tooltip>
						</Stack>
					);
				})}
			</Stack>
		);
	}

	if (title === "Source Documents") {
		return (
			<Container>
				<SubTitle title={title} />
				<PdfContainer documents={value} />
			</Container>
		);
	}

	return (
		<Container>
			<SubTitle title={title} />
			<SubValues value={value.join(", ")} />
		</Container>
	);
};

export const ArrayComponentForViolationRegulation: React.FC<{ title: string; value: string[] }> = ({
	title,
	value,
}) => {
	if (title.includes("Grass Types")) {
		return blueContainerForArray(value, title);
	}

	if (title === "Documents") {
		return (
			<Container>
				<SubTitle title={title} />
				<PdfContainer documents={value} />
			</Container>
		);
	}

	if (title === "Restricted" || title === "Mandatory" || title === "Regulated") {
		return (
			<Stack gap={1}>
				<SubTitle title={title} />
				{value.map((option: any, index: number) => {
					if (
						datastore.notVisibleItemOfViolationTab["Landscaping"]?.includes(
							option.option
						)
					)
						return null;
					if (datastore.notVisibleItemOfViolationTab["Trash"]?.includes(option.option))
						return null;
					if (datastore.notVisibleItemOfViolationTab["Parking"]?.includes(option.option))
						return null;
					if (datastore.notVisibleItemOfViolationTab["Nuisance"]?.includes(option.option))
						return null;
					if (
						datastore.notVisibleItemOfViolationTab["Structure"]?.includes(option.option)
					)
						return null;

					const regulationTitle = regulationDataChange(option.option);
					return (
						<Container
							sx={{
								py: 2,
								borderBottom:
									value.length - 1 !== index ? "1px solid #E8E8E8" : "none",
							}}
							key={index}
						>
							<span
								style={{
									width: "300px",
									minWidth: "300px",
									wordBreak: "break-word",
								}}
								className="subtitle-3"
							>
								{option.option}
							</span>
							<Stack gap={1}>
								{option.regulation && (
									<span className="subtitle-gray-14">{regulationTitle}</span>
								)}
								<span style={{ whiteSpace: 'pre-line' }} className="body-gray-16">{option.regulation}</span>
							</Stack>
						</Container>
					);
				})}
			</Stack>
		);
	}

	return (
		<Container>
			<SubTitle title={title} />
			<SubValues value={value.join(", ")} />
		</Container>
	);
};

export const ObjectComponentForViolationRegulation: React.FC<{ title: string; value: any }> = ({
	title,
	value,
}) => {
	if (title === "Prohibited Vehicles") {
		return (
			<Container>
				<Stack width={"300px"} flexDirection={"column"} gap={1}>
					<SubTitle title={title} />
					{blueArrayContainer(value.vehicles, title)}
				</Stack>
				<Stack gap={1}>
					<SubValues value={"Regulation"} />
					<SubValues value={value.regulation} />
				</Stack>
			</Container>
		);
	}

	return (
		<Container>
			<SubTitle title={title} />
		</Container>
	);
};

export const StringComponentForViolationRegulation: React.FC<{ title: string; value: string }> = ({
	title,
	value,
}) => {
	if (title === "Structure Hyper Link") {
		return <OpenANewLinkContainer title={"Hyper Link"} value={value} />;
	}

	if (title === "others") {
		return (
			<Container>
				<SubValues value={value} />
			</Container>
		);
	}

	return (
		<Container>
			<SubTitle title={title} />
			<SubValues value={value} />
		</Container>
	);
};
